/**
 * ContractsGuide — User guide for Contract Management module.
 */
import React from 'react';
import GuideViewer, { GuideSection, GuideSubSection, GuideCallout, GuideFieldTable, GuideTOC } from './GuideViewer';
import AppShell from '../../components/layout/AppShell';

function GuideContent() {
  const toc = [
    { id: 'overview', label: 'Overview & Purpose' },
    { id: 'dashboard', label: 'Contracts Dashboard' },
    { id: 'listing', label: 'Contracts Listing' },
    { id: 'creating', label: 'Creating a Contract' },
    { id: 'editor', label: 'The Contract Editor' },
    { id: 'workflow', label: 'Approval Workflow' },
    { id: 'clauses', label: 'Clause Library' },
    { id: 'documents', label: 'Document Attachments' },
    { id: 'renewal', label: 'Renewal & Expiry Tracking' },
    { id: 'reports', label: 'Contract Reports' },
  ];

  return (
    <GuideViewer title="Contracts Module" subtitle="Contract Management User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. Overview & Purpose">
        <p className="text-sm text-slate-600 mb-3">
          The Contracts module manages your supplier agreements throughout their full lifecycle: from initial drafting through internal review, legal review, execution, and renewal. It provides a centralized repository for all contract documents with version control, clause management, and automated expiry alerts.
        </p>
        <p className="text-sm text-slate-600 mb-3">
          Well-managed contracts ensure your organisation benefits from negotiated terms, enforces compliance requirements, and avoids costly lapses in coverage.
        </p>
      </GuideSection>

      <GuideSection id="dashboard" title="2. Contracts Dashboard">
        <p className="text-sm text-slate-600 mb-3">
          The Contracts Dashboard is the default view when you navigate to Contracts. It provides an at-a-glance overview:
        </p>
        <GuideFieldTable fields={[
          { name: 'KPI Strip', desc: 'Shows Total Contracts, Total Value, Active, Expiring within 90 days, Expired, and Average Value.' },
          { name: 'Risk Alerts', desc: 'Highlights contracts that are expired but still marked active, and contracts expiring within 90 days requiring renewal action.' },
          { name: 'Status Breakdown', desc: 'A donut chart showing contracts by workflow status (Draft, In Review, Active, Expired, etc.).' },
          { name: 'Top Suppliers', desc: 'A bar chart showing the highest-value contracts by supplier.' },
          { name: 'Expiry Timeline', desc: 'A bar chart showing how many contracts expire each month over the coming year.' },
          { name: 'Value by Manager', desc: 'Breakdown of total contract value by contract manager.' },
        ]} />
      </GuideSection>

      <GuideSection id="listing" title="3. Contracts Listing">
        <p className="text-sm text-slate-600 mb-3">
          Click the "List" tab to see all contracts in a sortable, filterable table:
        </p>
        <GuideFieldTable fields={[
          { name: 'Contract Number', desc: 'System-generated unique reference (e.g., "CON-000001").' },
          { name: 'Description', desc: 'Brief title describing the contract\'s purpose.' },
          { name: 'Status', desc: 'Current workflow status: Draft, Pending Review, In Legal Review, Active, Expired, Terminated.' },
          { name: 'Contract End', desc: 'The date the contract expires or terminates.' },
          { name: 'Active', desc: 'Whether the contract is currently in effect.' },
          { name: 'Value', desc: 'The total monetary value of the contract.' },
          { name: 'Manager', desc: 'The person responsible for managing this contract.' },
          { name: 'Supplier', desc: 'The contracting supplier.' },
        ]} />
      </GuideSection>

      <GuideSection id="creating" title="4. Creating a Contract">
        <p className="text-sm text-slate-600 mb-3">
          Click <strong>+ New Contract</strong> to start. You will be presented with a form to enter the key contract details:
        </p>
        <GuideFieldTable fields={[
          { name: 'Description', desc: 'A descriptive title for the contract.' },
          { name: 'Supplier', desc: 'Select the supplier from your supplier master. Start typing to search.' },
          { name: 'Contract Start / End', desc: 'The effective date range of the contract.' },
          { name: 'Review Date', desc: 'A reminder date for when the contract should be reviewed for renewal.' },
          { name: 'Contract Value', desc: 'The total monetary value.' },
          { name: 'Currency', desc: 'The currency for the contract value.' },
          { name: 'Contract Manager', desc: 'The internal person responsible for this contract.' },
          { name: 'Governing Law', desc: 'The legal jurisdiction governing the contract.' },
          { name: 'Payment Terms', desc: 'Payment terms (e.g., Net 30, Net 60).' },
          { name: 'Notice Period', desc: 'Required notice period for termination or non-renewal (in days).' },
          { name: 'Renewable', desc: 'Whether the contract auto-renews or requires explicit renewal.' },
          { name: 'Effective Date Type', desc: 'Whether the contract takes effect "On Execution" or on the specified start date.' },
        ]} />
      </GuideSection>

      <GuideSection id="editor" title="5. The Contract Editor">
        <p className="text-sm text-slate-600 mb-3">
          The Contract Editor is a rich-text document editor where you compose the actual contract language. It supports:
        </p>
        <GuideFieldTable fields={[
          { name: 'Rich Text Editing', desc: 'Bold, italic, underline, headings, lists, tables, and alignment controls.' },
          { name: 'Variable Insertion', desc: 'Insert placeholders (e.g., {{supplier_name}}, {{contract_start}}, {{today}}) that auto-resolve to actual values.' },
          { name: 'Clause Library', desc: 'Insert pre-approved standard clauses from your clause library with one click.' },
          { name: 'Track Changes', desc: 'Once a contract has been submitted for review, all edits are tracked with change marks showing who changed what.' },
          { name: 'Version History', desc: 'View previous versions of the contract document.' },
          { name: 'Auto-Save', desc: 'Changes are automatically saved as you type.' },
        ]} />
        <GuideCallout type="tip" title="Variable Placeholders">
          <p>Use the "Insert Variable" button to add dynamic placeholders. When the contract is printed or exported, these are replaced with actual values from the contract record (supplier name, dates, values, etc.).</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="workflow" title="6. Approval Workflow">
        <p className="text-sm text-slate-600 mb-3">
          Contracts follow a configurable approval workflow:
        </p>
        <ol className="list-decimal list-inside text-sm text-slate-600 space-y-2 mb-3">
          <li><strong>Draft</strong> — The contract is being composed. Only the creator can edit.</li>
          <li><strong>Internal Review</strong> — Submitted to internal stakeholders for review and comments.</li>
          <li><strong>Legal Review</strong> — Forwarded to legal counsel for review of terms and compliance.</li>
          <li><strong>Approved / Active</strong> — All reviews complete. The contract is in effect.</li>
          <li><strong>Expired / Terminated</strong> — The contract has ended or been terminated early.</li>
        </ol>
        <p className="text-sm text-slate-600 mb-2">
          At each stage, the assigned reviewers receive notifications and can approve, reject, or request changes with comments.
        </p>
      </GuideSection>

      <GuideSection id="clauses" title="7. Clause Library">
        <p className="text-sm text-slate-600 mb-3">
          The Clause Library is a repository of pre-approved standard contract clauses maintained by your legal or procurement team. When editing a contract, you can browse the library and insert clauses directly into the document. This ensures consistency and compliance across all contracts.
        </p>
      </GuideSection>

      <GuideSection id="documents" title="8. Document Attachments">
        <p className="text-sm text-slate-600 mb-3">
          Each contract can have multiple document attachments (PDFs, Word documents, images). These might include signed copies, supporting schedules, certificates, or amendments. Upload documents from the Attachments section of the contract detail view.
        </p>
      </GuideSection>

      <GuideSection id="renewal" title="9. Renewal & Expiry Tracking">
        <p className="text-sm text-slate-600 mb-3">
          The Contracts Dashboard automatically highlights contracts approaching expiry. The <strong>Review Date</strong> field triggers a reminder so the contract manager can initiate renewal discussions with the supplier before the contract lapses.
        </p>
        <GuideCallout type="warn" title="Expiry Alerts">
          <p>Contracts expiring within 90 days appear in the Dashboard's risk alert section. Expired contracts that are still marked Active are flagged as critical risks requiring immediate attention.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="reports" title="10. Contract Reports">
        <p className="text-sm text-slate-600 mb-3">
          The Reports tab provides detailed contract listings with filtering by status, supplier, manager, value range, and date range. All reports support CSV, PDF, and email export.
        </p>
      </GuideSection>
    </GuideViewer>
  );
}

export default function ContractsGuidePage() {
  return <AppShell><GuideContent /></AppShell>;
}
export function ContractsGuideEmbed() {
  return <GuideContent />;
}
